import { ChevronRight } from "lucide-react";
import { SlideHeader, Headline, DeepDive } from "../shared";
import { WhyNowRing } from "@/components/WhyNowRing";

export function Slide06WhyNow() {
  const LOGIC = [
    "AI can now personalize guidance in real time.",
    "Wearables are already collecting biometric, behavioral and environmental signals at massive scale.",
    "Consumers increasingly expect personalized skincare, wellness and health guidance.",
    "Skincare companies are investing heavily in AI diagnostics, predictive science and personalization.",
    "Sensors have become small and affordable enough to move into everyday wearable products.",
  ];
  const CHAIN = ["Sense", "Understand", "Prompt", "Act", "Learn"];
  const FORCES = [
    ["AI Has Become Personal", "AI can interpret context, routines, environmental conditions, formulation use and wearable signals to deliver guidance that becomes more relevant over time."],
    ["The Wrist Has Become a Trusted Health Interface", "More than 200 million wearable devices shipped globally in 2025. Consumers now accept continuous sensing, biometric tracking and personalized guidance as part of daily life."],
    ["Skincare Is Shifting From Products to Outcomes", "Consumers increasingly want science-backed, personalized solutions. But even the best formulation cannot deliver its intended benefit if it is unavailable, forgotten, or inconsistently applied."],
    ["Wellness Has Become Continuous and Preventive", "The global wellness economy has reached approximately $2 trillion. Younger consumers increasingly treat wellness as a daily, personalized practice rather than an occasional purchase."],
    ["Longevity Is Reshaping Beauty", "Consumers are moving beyond short-term cosmetic correction toward prevention, long-term skin health, healthy aging, and maintaining appearance over time."],
    ["Beauty Brands Need New Growth Platforms", "Global beauty sales grew 10% year over year, while brands face growing pressure to deliver convenience, personalization, measurable engagement, and differentiated consumer experiences."],
  ];
  return (
    <div className="max-w-5xl" data-testid="pitch-slide-whynow">
      <SlideHeader n="06" label="Why Now" />
      <Headline>
        AI and wearable sensing can finally understand{" "}
        <span className="text-accent">when personal care is needed.</span>
      </Headline>
      <p className="font-heading text-white text-lg md:text-xl font-medium mt-4 max-w-2xl">
        But understanding is not enough. <span className="text-accent">The product still has to be there.</span>
      </p>
      <div className="grid lg:grid-cols-2 gap-x-10 gap-y-4 mt-5">
        <div className="space-y-2" data-testid="pitch-whynow-logic">
          {LOGIC.map((t) => (
            <div key={t} className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 mt-2 shrink-0" />
              <p className="font-body text-zinc-300 text-sm md:text-base leading-relaxed">{t}</p>
            </div>
          ))}
        </div>
        <div className="pt-4 lg:pt-0 lg:pl-8 lg:border-l border-t lg:border-t-0 border-zinc-800" data-testid="pitch-whynow-closing">
          <div className="space-y-1">
            <p className="font-body text-zinc-400 text-sm md:text-base leading-relaxed">WearSpray completes the loop.</p>
            <p className="font-body text-zinc-400 text-sm md:text-base leading-relaxed">The intelligence recognizes the moment.</p>
            <p className="font-body text-zinc-400 text-sm md:text-base leading-relaxed">The formulation is already on the wrist.</p>
            <p className="font-body text-white text-sm md:text-base leading-relaxed font-medium">The user can act immediately.</p>
          </div>
          <p className="font-heading text-white text-base md:text-lg font-medium mt-4">
            Most wearable technology stops at awareness.{" "}
            <span className="text-accent">WearSpray adds the action layer.</span>
          </p>
          <div className="pt-2">
            <DeepDive testid="whynow" title="Why Now, In Detail">
              <div className="flex justify-center mb-8">
                <WhyNowRing />
              </div>
              <div className="space-y-5 mt-2">
                {FORCES.map(([t, d], i) => (
                  <div key={t}>
                    <p className="font-heading text-white text-base font-medium">{i + 1}. {t}</p>
                    <p className="font-body text-zinc-400 text-sm leading-relaxed mt-1">{d}</p>
                  </div>
                ))}
              </div>
              <div className="mt-7 pt-5 border-t border-zinc-800">
                <p className="font-heading text-white text-base font-medium">The Convergence Creates the Opportunity</p>
                <p className="font-body text-zinc-500 text-sm leading-relaxed mt-2">
                  <span className="text-zinc-400 font-medium">Previously:</span> Products lived in bags. Guidance
                  lived in apps. Wearable data stopped at awareness.
                </p>
                <p className="font-body text-zinc-300 text-sm leading-relaxed mt-2">
                  <span className="text-white font-medium">Today:</span> AI can understand the individual.
                  Wearables can sense context. WearSpray can turn the right prompt into immediate action.
                </p>
              </div>
            </DeepDive>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2 mt-4" data-testid="pitch-whynow-chain">
        {CHAIN.map((w, i) => (
          <div key={w} className="flex items-center gap-1.5">
            <span className="font-mono text-[11px] sm:text-xs tracking-[0.14em] uppercase text-accent border border-accent/40 bg-accent/[0.07] rounded-full px-3.5 py-1.5 whitespace-nowrap">
              {w}
            </span>
            {i < CHAIN.length - 1 && <ChevronRight size={13} className="text-zinc-600 shrink-0" aria-hidden="true" />}
          </div>
        ))}
      </div>
    </div>
  );
}
